import { BasketItem, PaymentOption } from '@reward-platform/order-schemas/basket'
import {
  CarHireItemType,
  ExperienceItemType,
  FlightItemType,
  HotelItemType,
} from '@reward-platform/order-schemas/common'
import { SelectedPaymentOptions } from '@reward-platform/order-schemas/form-data'
import usePaymentOptions from '~/components/orders/hooks/storage/usePaymentOptions'
import { useUser } from '~/context/user'
import { useAccount } from '~/context/account'
import { PromotionObject } from '@reward-platform/ancillaries-schemas/hotel'
import useBasket from '../useBasket/useBasket'

const sortPaymentOptionsByDescendingAviosValue = (paymentOptions: PaymentOption[]) =>
  paymentOptions.slice().sort((a, b) => b.aviosValue - a.aviosValue)

/**
 * Returns the default payment option id for a given basket item
 * If the user has enough avios to buy all items in basket with their highest avios amounts, then
 * return the payment option with the highest amount of avios.
 *
 * Otherwise:
 * For ancillary items, it will return the only cash option if it exists
 * For flights, it will return the highest avios option that the user can afford
 * */
const getDefaultPaymentOptionId = (
  basketItem: BasketItem,
  userAviosBalance = 0,
  basketAviosBalance = 0
): string => {
  const {
    id: basketItemId,
    type,
    paymentOptions: { onlyCashPaymentOption, options },
  } = basketItem

  const getDefaultPaymentOptionIdForBasketItem = () => {
    const sortedPaymentOptions = sortPaymentOptionsByDescendingAviosValue(options)
    const optionWithHighestAmountOfAvios = sortedPaymentOptions.at(0)
    if (
      optionWithHighestAmountOfAvios &&
      userAviosBalance >= basketAviosBalance &&
      userAviosBalance >= optionWithHighestAmountOfAvios.aviosValue
    ) {
      return optionWithHighestAmountOfAvios.id
    }

    if (type === FlightItemType) {
      const paymentOption =
        sortedPaymentOptions.find(({ aviosValue }) => aviosValue <= userAviosBalance) ??
        sortedPaymentOptions.at(-1)

      return paymentOption?.id
    }

    return onlyCashPaymentOption?.id
  }

  const paymentOptionId = getDefaultPaymentOptionIdForBasketItem()

  if (paymentOptionId) {
    return paymentOptionId
  }

  throw new Error(`No default payment option found for ${type} basket item ${basketItemId}`)
}

const getDefaultPaymentOptionPromotion = (
  basketItem: BasketItem,
  paymentOptionId: string
): PromotionObject[] => {
  const {
    paymentOptions: { onlyCashPaymentOption, options },
  } = basketItem

  if (onlyCashPaymentOption && paymentOptionId === onlyCashPaymentOption.id) {
    return onlyCashPaymentOption.promotions || []
  }

  const foundPaymentOption = options.find((x) => x.id === paymentOptionId)

  if (foundPaymentOption) {
    return foundPaymentOption.promotions || []
  }

  return []
}

export const useDefaultPaymentOptions = (): SelectedPaymentOptions | undefined => {
  const {
    basket: { data: basket },
  } = useBasket()

  const { storage } = usePaymentOptions(true)
  const { user } = useUser()
  const { account, aviosBalance } = useAccount()

  if (!basket || (user && !account)) {
    return undefined
  }

  const basketItems: BasketItem[] = [
    basket?.items[HotelItemType]?.[0],
    basket?.items[CarHireItemType]?.[0],
    basket?.items[ExperienceItemType]?.[0],
    basket?.items[FlightItemType]?.[0],
  ].filter((item): item is BasketItem => Boolean(item))

  const totalBasketAviosBalance = basketItems
    .flatMap(({ id: basketItemId, type, paymentOptions }) => {
      let currentAmmountOfAvios = 0
      if (storage?.[type]?.basketItemId === basketItemId) {
        const selectedPaymentOptionId = storage[type]?.paymentOptionId
        const foundPaymentOption = paymentOptions.options.find(
          (x) => x.id === selectedPaymentOptionId
        )
        if (foundPaymentOption) {
          currentAmmountOfAvios = foundPaymentOption.aviosValue
        }
      }
      return [currentAmmountOfAvios]
    })
    .reduce((acc, aviosValue) => acc + aviosValue, 0)

  const userAviosBalance = aviosBalance ?? 0

  return basketItems.reduce((acc, basketItem) => {
    const { id: basketItemId, type } = basketItem
    const defaultSelectedPaymentOptionId = getDefaultPaymentOptionId(
      basketItem,
      userAviosBalance,
      totalBasketAviosBalance
    )
    const option =
      storage?.[type]?.basketItemId === basketItemId && totalBasketAviosBalance <= userAviosBalance
        ? storage[type]
        : {
            basketItemId,
            paymentOptionId: defaultSelectedPaymentOptionId,
            selectedByDefault: true,
            promotions: getDefaultPaymentOptionPromotion(
              basketItem,
              defaultSelectedPaymentOptionId
            ),
          }

    return {
      ...acc,
      [type]: option,
    }
  }, {})
}
